import { Link, useNavigate } from 'react-router-dom'
import { Search, Menu, Heart, MessageSquare, Plus, User, LogOut, Settings, ChevronDown, Bug, Store, ShoppingCart, Package } from 'lucide-react'
import { useState, useRef, useEffect, memo } from 'react'
import { clsx } from 'clsx'
import { signOut } from 'firebase/auth'
import { auth } from '@/services/firebase/config'
import { useAuthStore, isStoreAccount } from '@/store/authStore'
import { useUIStore } from '@/store/uiStore'
import { Button } from '@/components/common/Button'
import { MegaMenu } from '@/components/layout/MegaMenu'
import { getAvatarUrl } from '@/utils/avatar'
import { useCartStore } from '@/store/cartStore'
import { NotificationBell } from '@/components/notifications/NotificationBell'
import { useUnreadCount } from '@/hooks'

export const Header = memo(function Header() {
  const navigate = useNavigate()
  const { user, profile, isAuthenticated, logout } = useAuthStore()
  const { openModal, toggleMobileMenu, mobileMenuOpen, closeMobileMenu } = useUIStore()
  const { itemCount, openDrawer: openCart } = useCartStore()
  const [searchQuery, setSearchQuery] = useState('')
  const [userDropdownOpen, setUserDropdownOpen] = useState(false)
  const dropdownRef = useRef<HTMLDivElement>(null)
  const unreadMessageCount = useUnreadCount()

  // Close dropdown when clicking outside or pressing Escape
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setUserDropdownOpen(false)
      }
    }
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && userDropdownOpen) {
        setUserDropdownOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    document.addEventListener('keydown', handleEscape)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
      document.removeEventListener('keydown', handleEscape)
    }
  }, [userDropdownOpen])

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault()
    if (searchQuery.trim()) {
      navigate(`/browse?q=${encodeURIComponent(searchQuery.trim())}`)
    }
  }

  const handleLogout = async () => {
    await signOut(auth)
    logout()
    setUserDropdownOpen(false)
    navigate('/')
  }

  return (
    <header className="sticky top-0 z-40 bg-white border-b border-slate-200">
      <div className="container">
        <div className="flex items-center justify-between h-16 gap-4">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2 flex-shrink-0">
            <img
              src="/images/old-logo.png"
              alt="iSQROLL"
              className="h-8 w-auto"
            />
          </Link>

          {/* Search bar - desktop */}
          <form onSubmit={handleSearch} className="hidden md:flex flex-1 max-w-xl">
            <div className="relative w-full">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
              <input
                type="search"
                placeholder="Search listings..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="input pl-10 pr-4"
                autoComplete="off"
              />
            </div>
          </form>

          {/* Desktop nav */}
          <nav className="hidden md:flex items-center gap-2">
            <Link to="/browse" className="btn-ghost text-sm">
              Browse
            </Link>
            <MegaMenu />

            {isAuthenticated ? (
              <>
                <Link to="/messages" className="btn-ghost p-2 relative" aria-label="Messages">
                  <MessageSquare className="w-5 h-5" />
                  {unreadMessageCount > 0 && (
                    <span
                      className="absolute -top-1 -right-1 bg-red-500 text-white text-xs font-bold min-w-[20px] h-5 rounded-full flex items-center justify-center px-1"
                      aria-label={`${unreadMessageCount} unread messages`}
                    >
                      {unreadMessageCount > 99 ? '99+' : unreadMessageCount}
                    </span>
                  )}
                </Link>
                <Link to="/watchlist" className="btn-ghost p-2" aria-label="Watchlist">
                  <Heart className="w-5 h-5" />
                </Link>
                <button
                  onClick={openCart}
                  className="btn-ghost p-2 relative"
                  aria-label="Cart"
                >
                  <ShoppingCart className="w-5 h-5" />
                  {itemCount > 0 && (
                    <span
                      className="absolute -top-1 -right-1 bg-accent-500 text-white text-xs font-bold w-5 h-5 rounded-full flex items-center justify-center"
                      aria-label={`${itemCount} items in cart`}
                    >
                      {itemCount > 99 ? '99+' : itemCount}
                    </span>
                  )}
                </button>
                <NotificationBell />

                {/* User dropdown */}
                <div className="relative" ref={dropdownRef}>
                  <button
                    onClick={() => setUserDropdownOpen(!userDropdownOpen)}
                    className="btn-ghost flex items-center gap-2"
                    aria-expanded={userDropdownOpen}
                    aria-haspopup="menu"
                  >
                    <div className="w-8 h-8 rounded-full bg-primary-500 text-white flex items-center justify-center text-sm font-medium overflow-hidden">
                      {profile && getAvatarUrl(profile) ? (
                        <img
                          src={getAvatarUrl(profile)!}
                          alt={`${user?.displayName || 'User'} avatar`}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        user?.displayName?.[0]?.toUpperCase() || 'U'
                      )}
                    </div>
                    <ChevronDown className={clsx('w-4 h-4 transition-transform', userDropdownOpen && 'rotate-180')} />
                  </button>

                  {userDropdownOpen && (
                    <div className="absolute right-0 mt-2 w-56 bg-white rounded-lg shadow-lg border border-slate-200 py-2 animate-fade-in">
                      <div className="px-4 py-2 border-b border-slate-100">
                        <p className="font-medium text-slate-900 truncate">{user?.displayName || 'User'}</p>
                        <p className="text-sm text-slate-500 truncate">{user?.email}</p>
                      </div>

                      <Link
                        to="/profile"
                        onClick={() => setUserDropdownOpen(false)}
                        className="flex items-center gap-3 px-4 py-2 text-sm text-slate-700 hover:bg-slate-50"
                      >
                        <User className="w-4 h-4" />
                        {isStoreAccount(profile) ? 'My Store' : 'Profile'}
                      </Link>
                      {user && (
                        <Link
                          to={profile?.storeSlug ? `/store/${profile.storeSlug}` : `/seller/${user.uid}`}
                          onClick={() => setUserDropdownOpen(false)}
                          className="flex items-center gap-3 px-4 py-2 text-sm text-slate-700 hover:bg-slate-50"
                        >
                          <Store className="w-4 h-4" />
                          {isStoreAccount(profile) ? 'View Store' : 'View Profile'}
                        </Link>
                      )}
                      <Link
                        to="/my-listings"
                        onClick={() => setUserDropdownOpen(false)}
                        className="flex items-center gap-3 px-4 py-2 text-sm text-slate-700 hover:bg-slate-50"
                      >
                        <Package className="w-4 h-4" />
                        My Listings
                      </Link>
                      <Link
                        to="/profile/settings"
                        onClick={() => setUserDropdownOpen(false)}
                        className="flex items-center gap-3 px-4 py-2 text-sm text-slate-700 hover:bg-slate-50"
                      >
                        <Settings className="w-4 h-4" />
                        Account Settings
                      </Link>

                      <Link
                        to="/bug-report"
                        onClick={() => setUserDropdownOpen(false)}
                        className="flex items-center gap-3 px-4 py-2 text-sm text-slate-700 hover:bg-slate-50"
                      >
                        <Bug className="w-4 h-4" />
                        Report a Bug
                      </Link>

                      <hr className="my-2 border-slate-100" />

                      <button
                        onClick={handleLogout}
                        className="flex items-center gap-3 px-4 py-2 text-sm text-red-600 hover:bg-red-50 w-full"
                      >
                        <LogOut className="w-4 h-4" />
                        Log Out
                      </button>
                    </div>
                  )}
                </div>

                <Link to="/create">
                  <Button variant="accent" leftIcon={<Plus className="w-4 h-4" />}>
                    Sell
                  </Button>
                </Link>
              </>
            ) : (
              <>
                <Button variant="ghost" onClick={() => openModal('login')}>
                  Log In
                </Button>
                <Link to="/signup">
                  <Button variant="accent">
                    Sign Up
                  </Button>
                </Link>
              </>
            )}
          </nav>

          {/* Mobile menu button */}
          <button
            onClick={toggleMobileMenu}
            className="md:hidden btn-ghost p-2"
            aria-label="Menu"
          >
            <Menu className="w-6 h-6" />
          </button>
        </div>

        {/* Mobile search */}
        <form onSubmit={handleSearch} className="md:hidden pb-3">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
            <input
              type="search"
              placeholder="Search listings..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="input pl-10 pr-4"
              autoComplete="off"
            />
          </div>
        </form>
      </div>

      {/* Mobile menu overlay */}
      {mobileMenuOpen && (
        <div className="fixed inset-0 z-50 md:hidden">
          <div className="absolute inset-0 bg-black/50" onClick={closeMobileMenu} />
          <nav className="absolute right-0 top-0 h-full w-80 max-w-full bg-white shadow-xl animate-slide-up">
            <div className="p-4 border-b border-slate-200 flex items-center justify-between">
              <span className="font-semibold text-lg">Menu</span>
              <button onClick={closeMobileMenu} className="p-2 hover:bg-slate-100 rounded-lg">
                <Menu className="w-5 h-5" />
              </button>
            </div>

            <div className="p-4 space-y-2">
              <Link to="/browse" onClick={closeMobileMenu} className="block py-3 px-4 rounded-lg hover:bg-slate-50">
                Browse Listings
              </Link>
              <Link to="/categories" onClick={closeMobileMenu} className="block py-3 px-4 rounded-lg hover:bg-slate-50">
                Categories
              </Link>
              <Link to="/about" onClick={closeMobileMenu} className="block py-3 px-4 rounded-lg hover:bg-slate-50">
                About
              </Link>

              <hr className="my-4" />

              {isAuthenticated ? (
                <>
                  <Link to="/profile" onClick={closeMobileMenu} className="block py-3 px-4 rounded-lg hover:bg-slate-50">
                    {isStoreAccount(profile) ? 'My Store' : 'Profile'}
                  </Link>
                  {user && (
                    <Link to={profile?.storeSlug ? `/store/${profile.storeSlug}` : `/seller/${user.uid}`} onClick={closeMobileMenu} className="block py-3 px-4 rounded-lg hover:bg-slate-50">
                      {isStoreAccount(profile) ? 'View Store' : 'View Profile'}
                    </Link>
                  )}
                  <Link to="/my-listings" onClick={closeMobileMenu} className="block py-3 px-4 rounded-lg hover:bg-slate-50">
                    My Listings
                  </Link>
                  <Link to="/profile/settings" onClick={closeMobileMenu} className="block py-3 px-4 rounded-lg hover:bg-slate-50">
                    Account Settings
                  </Link>
                  <Link to="/bug-report" onClick={closeMobileMenu} className="block py-3 px-4 rounded-lg hover:bg-slate-50">
                    Report a Bug
                  </Link>
                  <Link to="/create" onClick={closeMobileMenu}>
                    <Button variant="accent" fullWidth leftIcon={<Plus className="w-4 h-4" />}>
                      Create Listing
                    </Button>
                  </Link>

                  <hr className="my-4" />

                  <button
                    onClick={() => {
                      closeMobileMenu()
                      handleLogout()
                    }}
                    className="flex items-center gap-3 py-3 px-4 rounded-lg text-red-600 hover:bg-red-50 w-full"
                  >
                    <LogOut className="w-5 h-5" />
                    Log Out
                  </button>
                </>
              ) : (
                <div className="space-y-2">
                  <Button
                    variant="primary"
                    fullWidth
                    onClick={() => {
                      closeMobileMenu()
                      openModal('login')
                    }}
                  >
                    Log In
                  </Button>
                  <Link to="/signup" onClick={closeMobileMenu}>
                    <Button variant="accent" fullWidth>
                      Sign Up
                    </Button>
                  </Link>
                </div>
              )}
            </div>
          </nav>
        </div>
      )}
    </header>
  )
})
